/**
 * Samtycke till behandling av särskilda kategorier av personuppgifter —
 * GDPR artikel 9.
 *
 * Issued as a bilaga riding its parent lease's signing
 * (`samtycke_sarskilda_bilaga`) and as a standalone, signed
 * `samtycke_sarskilda_avtal`. Sections 1–8 are shared verbatim; only the
 * slot annotations of the avtalsref line and § 1 (derived from the parent
 * lease vs autofilled from the parties) and the closing § 9 differ.
 *
 * Legal text verbatim from the approved Word source — do not paraphrase or
 * reorder.
 */
import { nodes, option, optionList, p, section, strong, t, val } from '@hyranai/regta-document-ir';
import { multiSelectSlot } from '../lib.js';
import { optionChosen, slotText, textVal, ticked, whenIncludes } from '../common/index.js';
import { autoClause, gdprPartySlot, gdprRows, legalBase } from '../personuppgiftsbilaga/text.js';
export const SOURCE = 'base-templates/BILAGA  – SAMTYCKE TILL BEHANDLING AV SÄRSKILDA KATEGORIER A.docx';
export const PREAMBLE = 'Hyresgästens samtycke till behandling av särskilda kategorier av personuppgifter (GDPR artikel 9).';
export const SAMTYCKE_TEXT = 'Jag samtycker härmed uttryckligen till att hyresvärden behandlar ovan angivna särskilda kategorier av personuppgifter för de ändamål som anges i denna bilaga.';
/** Codes equal the legacy stored values, so no `value` is needed. */
export const CATEGORY_OPTIONS = [
    { code: 'halsa', label: 'Uppgifter om hälsa eller funktionsnedsättning', summary: 'Data on health or disability.' },
    { code: 'bostadsanpassning', label: 'Uppgifter om behov av bostadsanpassning', summary: 'Data on the need for home adaptation.' },
    {
        code: 'vardgivare',
        label: 'Uppgifter från läkare, arbetsterapeut eller annan vårdgivare',
        summary: 'Data from a doctor, occupational therapist or other care provider.',
    },
    { code: 'kommun', label: 'Uppgifter från kommun eller socialtjänst', summary: 'Data from the municipality or social services.' },
    {
        code: 'annat',
        label: 'Annat',
        summary: 'Another category of sensitive data, specified in free text.',
        detailKey: 'categories_other',
    },
];
const ANNAT = CATEGORY_OPTIONS[4];
/** "Bilaga till hyresavtal avseende lägenhet/lokal nr: …" above § 1. */
export function avtalsrefBlock(blockKey, variant) {
    const slot = {
        key: 'apartment_ref',
        label: 'Avtal / lägenhet nr',
        type: 'text',
        required: true,
        ...(variant === 'standalone'
            ? {}
            : { derivedFromParent: true, derivedFrom: 'contract_number', help: 'Hämtas från hyresavtalet.' }),
    };
    return {
        ...legalBase(SOURCE),
        blockKey,
        version: 'v1',
        title: 'Avtalsreferens',
        summary: 'The lease / apartment number the consent belongs to.',
        slots: [slot],
        compile: (input) => [p([t('Bilaga till hyresavtal avseende lägenhet/lokal nr: '), textVal(input, 'apartment_ref')])],
    };
}
export function parterBlock(blockKey, variant) {
    return {
        ...legalBase(SOURCE),
        blockKey,
        version: 'v1',
        title: 'Parter',
        summary: 'Landlord (name, organisation number, address) and tenant (name, personnummer, address).',
        slots: [
            gdprPartySlot(variant, { key: 'landlord_name', label: 'Namn / Firma', role: 'lessor', field: 'name', required: true }),
            gdprPartySlot(variant, { key: 'landlord_org_number', label: 'Organisationsnummer', role: 'lessor', field: 'legalId', required: true }),
            gdprPartySlot(variant, { key: 'landlord_address', label: 'Adress (hyresvärd)', role: 'lessor', field: 'address' }),
            gdprPartySlot(variant, { key: 'tenant_name', label: 'Namn (hyresgäst)', role: 'lessee', field: 'name', required: true }),
            gdprPartySlot(variant, {
                key: 'tenant_personnummer',
                label: 'Personnummer',
                role: 'lessee',
                field: 'legalId',
                required: true,
                note: 'PII — ej sökbart.',
            }),
            // bostadshyra carries no separate tenant address, so the bilaga's is plain.
            gdprPartySlot(variant, { key: 'tenant_address', label: 'Adress (hyresgäst)', role: 'lessee', field: 'address', fromParent: false }),
        ],
        compile: (input) => [
            section('parter', 'Parter', nodes(p([strong('Hyresvärd')], { keepWithNext: true }), gdprRows(input, [
                ['Namn / Firma', 'landlord_name'],
                ['Organisationsnummer', 'landlord_org_number'],
                ['Adress', 'landlord_address'],
            ]), p([strong('Hyresgäst')], { keepWithNext: true }), gdprRows(input, [
                ['Namn', 'tenant_name'],
                ['Personnummer', 'tenant_personnummer'],
                ['Adress', 'tenant_address'],
            ]))),
        ],
    };
}
export const avtalsref = avtalsrefBlock('samtycke_sarskilda_bilaga.avtalsref', 'bilaga');
export const parter = parterBlock('samtycke_sarskilda_bilaga.parter', 'bilaga');
export const bakgrund = autoClause({
    blockKey: 'samtycke_sarskilda_bilaga.bakgrund',
    ref: 'bakgrund',
    title: 'Bakgrund',
    summary: 'Why article-9 data is processed: the tenant has supplied, or will supply, data needed for housing-related needs.',
    source: SOURCE,
    body: [
        'Hyresgästen har lämnat eller kommer att lämna uppgifter som omfattas av artikel 9 i Europaparlamentets och rådets förordning (EU) 2016/679 (GDPR) och som är nödvändiga för att hyresvärden ska kunna bedöma, administrera eller tillgodose behov kopplade till bostaden eller hyresförhållandet.',
    ],
});
/**
 * § 3 — the special categories the consent covers. A blank printable form
 * leaves every box unticked so the tenant ticks their own (explicit consent).
 */
export const uppgifter = {
    ...legalBase(SOURCE),
    blockKey: 'samtycke_sarskilda_bilaga.uppgifter',
    version: 'v1',
    title: 'Uppgifter som omfattas av samtycket',
    summary: 'Checkbox list of the special categories of personal data covered by the consent, with a free-text "Annat".',
    slots: [
        multiSelectSlot('categories', 'Uppgifter som omfattas av samtycket', CATEGORY_OPTIONS, { required: true }),
        {
            key: 'categories_other',
            label: 'Annat (specificera)',
            type: 'text',
            visibleWhen: whenIncludes('categories', ANNAT),
            requiredWhen: whenIncludes('categories', ANNAT),
        },
    ],
    compile: (input, ctx) => {
        const has = (o) => ticked(ctx, optionChosen(CATEGORY_OPTIONS, input.categories, o));
        return [
            section('uppgifter', 'Uppgifter som omfattas av samtycket', [
                p('Samtycket omfattar följande uppgifter:'),
                optionList(CATEGORY_OPTIONS.map((o) => o === ANNAT
                    ? option(has(o), 
                    // Legacy: an unticked "Annat:" on a filled form has nothing to fill in after it.
                    [
                        t('Annat: '),
                        ...(has(o) || ctx.mode === 'blank'
                            ? [val(has(o) ? slotText(input.categories_other) : null, { sourceKey: 'categories_other', blankWidth: 'long' })]
                            : []),
                    ], { code: o.code })
                    : option(has(o), o.label, { code: o.code })), { sourceKey: 'categories', select: 'multi' }),
            ]),
        ];
    },
};
export const andamal = autoClause({
    blockKey: 'samtycke_sarskilda_bilaga.andamal',
    ref: 'andamal',
    title: 'Ändamål med behandlingen',
    summary: 'The purposes for which the special-category data may be processed.',
    source: SOURCE,
    body: [
        'Personuppgifterna får behandlas för att:',
        {
            bullets: [
                'bedöma behov av anpassad bostad,',
                'genomföra bostadsanpassningar,',
                'administrera särskilda boendebehov,',
                'fullgöra hyresvärdens skyldigheter enligt lag,',
                'hantera ärenden som hyresgästen själv initierat,',
                'fastställa, göra gällande eller försvara rättsliga anspråk.',
            ],
        },
    ],
});
export const mottagare = autoClause({
    blockKey: 'samtycke_sarskilda_bilaga.mottagare',
    ref: 'mottagare',
    title: 'Mottagare av uppgifterna',
    summary: 'Who the special-category data may be disclosed to.',
    source: SOURCE,
    body: [
        'Personuppgifterna får lämnas till:',
        {
            bullets: [
                'fastighetsförvaltare,',
                'tekniska konsulter och entreprenörer,',
                'kommun eller myndighet när det krävs för ändamålet,',
                'personuppgiftsbiträden som behandlar uppgifter för hyresvärdens räkning.',
            ],
        },
    ],
});
export const lagringstid = autoClause({
    blockKey: 'samtycke_sarskilda_bilaga.lagringstid',
    ref: 'lagringstid',
    title: 'Lagringstid',
    summary: 'Retention: only as long as needed for the stated purposes or required by law.',
    source: SOURCE,
    body: ['Uppgifterna behandlas endast så länge de behövs för de ändamål som anges ovan eller så länge lag kräver.'],
});
export const frivillighet = autoClause({
    blockKey: 'samtycke_sarskilda_bilaga.frivillighet',
    ref: 'frivillighet',
    title: 'Frivillighet och återkallelse',
    summary: 'Consent is voluntary and may be withdrawn in writing at any time, without retroactive effect.',
    source: SOURCE,
    body: [
        'Samtycket är frivilligt.',
        'Hyresgästen har rätt att när som helst återkalla sitt samtycke genom skriftligt meddelande till hyresvärden.',
        'Ett återkallande påverkar inte lagligheten av behandling som skett innan samtycket återkallades.',
    ],
});
export const rattigheter = autoClause({
    blockKey: 'samtycke_sarskilda_bilaga.rattigheter',
    ref: 'rattigheter',
    title: 'Registrerades rättigheter',
    summary: 'The data subject’s rights, including complaint to IMY.',
    source: SOURCE,
    body: [
        'Hyresgästen har rätt att:',
        {
            bullets: [
                'begära information om behandlingen,',
                'begära registerutdrag,',
                'begära rättelse,',
                'begära radering när lagens förutsättningar är uppfyllda,',
                'begära begränsning av behandling,',
                'inge klagomål till Integritetsskyddsmyndigheten (IMY).',
            ],
        },
    ],
});
/** § 9 on the bilaga: the consent sentence, signed as part of the parent lease. */
export const samtycke = {
    ...legalBase(SOURCE),
    blockKey: 'samtycke_sarskilda_bilaga.samtycke',
    version: 'v1',
    title: 'Samtycke',
    summary: 'The tenant’s explicit consent; signed as part of the parent lease, not on this bilaga.',
    slots: [],
    compile: () => [
        section('samtycke', 'Samtycke', [
            p(SAMTYCKE_TEXT),
            p('Denna bilaga utgör en del av hyresavtalet och undertecknas elektroniskt tillsammans med det. Egen underskrift krävs inte på denna handling.', { variant: 'fine_print' }),
        ]),
    ],
};
/** § 2 and §§ 3–8 in document order, shared verbatim by both variants. */
export const SAMTYCKE_SHARED_SECTIONS = [bakgrund, uppgifter, andamal, mottagare, lagringstid, frivillighet, rattigheter];
export const SAMTYCKE_SARSKILDA_BILAGA_BLOCKS = [avtalsref, parter, ...SAMTYCKE_SHARED_SECTIONS, samtycke];
//# sourceMappingURL=blocks.js.map